import { asyncpipe, curry } from 'lib/util'
import searchDevice from 'app/_shared/events/search/device'
import { settings } from 'app/_shared/session'
import { empty } from 'lib/util/object'

/**
 * Returns the first `ref` of `model`.
 * @param {Object} model  the model object
 * @param {string} ref    the name of th ref
 * @returns {*}
 */
const _firstRef = (model, ref) => model?.refs?.[ref]?.[0]

/**
 * Returns an object containing `serial`, `name`, and the relevant relationships of `serviceItem`.
 *
 * @param {Object} serviceItem
 * @returns {Object}
 */
export const expose = (serviceItem) => {
  if (!serviceItem || serviceItem?.serviceItem) {
    return serviceItem
  }
  const name = serviceItem?.value?.name
  const installedAt = _firstRef(serviceItem, 'installedAt')
  const equipment = _firstRef(serviceItem, 'equipment')
  const product = _firstRef(serviceItem, 'product')

  return {
    name,
    serviceItem,
    installedAt,
    equipment,
    product,
  }
}

/**
 * Obtains the serial from `eventOrSerial`, accepting that it can be found inside an event
 * object triggered by an input or directly the value.
 *
 * @param {Gaia.PlatformEvent|string} eventOrSerial information about the event that triggered this
 *                                                  handler or a serial number
 * @param {Object} [eventOrSerial.detail]           relevant information about the event
 * @param {string} [eventOrSerial.detail.value]     a serial number
 * @returns {string}
 */
const readSerial = eventOrSerial => eventOrSerial?.detail?.value ?? eventOrSerial

/**
 * If the {@link settings}'s `serialFieldConfig` property has a `validator` defined, it returns
 * `serial` only if it matches checking the validator regex. Otherwise, if there is no match or no
 * validator defined, returns an empty object.
 *
 * @param {string} serial   string to validate
 * @returns {Object|string} `serial` if valid or no `validator` is defined or else an empty object
 */
const validate = (serial) => {
  const match = settings.serialFieldConfig?.validator
    ? new RegExp(settings.serialFieldConfig.validator).test(serial)
    : true

  return match ? serial : {}
}

/**
 * Attempts to obtain a device by its `serial` by calling {@link searchDevice}.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {Gaia.Component.Spec} component  the current action's main component
 * @param {number} serial               a serial number
 * @returns {Promise<{serviceItem: *, product: *, serial, installedAt: *, equipment: (*)}>}
 */
const search = curry(async (module, component, serial) => {
  if (empty(serial)) {
    return [{ invalid: true }]
  }

  if (!serial) {
    return [{ serial }]
  }

  const data = await searchDevice('verbose', module, component, serial)

  const serviceItems = settings.serialFieldConfig?.strict
    ? data.filter(item => item.value.serial === serial || item.value.numberCommission === serial)
    : data

  return serviceItems.map(expose)
})

/**
 * Event Handler Find Device
 *
 * Attempts to find a device by its serial value.
 *
 * Attempts to find a service item with the same serial in the first place to inform the user that
 * such ServiceItem already exists and, in that case, its equipment is used. If no ServiceItem is
 * found, proceeds with the attempt to obtain the equipment itself.
 *
 * @type {Gaia.AppModule.EventHandler}
 * @param {Gaia.PlatformEvent|number} eventOrSerial information about the event that triggered this
 *                                                  handler or a serial number
 * @param {Object} [eventOrSerial.target]           information about the target of the event
 * @param {number} [eventOrSerial.target.value]     a serial number
 * @returns {Promise<object>}
 */
export default curry((module, component, eventOrSerial) => asyncpipe(
  readSerial,
  validate,
  search(module, component),
)(eventOrSerial))
